import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { sendToAndroidPrint } from '../utils/androidPrint';

export type PrintJobStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'retrying';

export interface PrintJob {
  id: string;
  orderId: string;
  ticketContent: string;
  profileId?: string;
  profileName?: string;
  copyNumber: number;
  totalCopies: number;
  status: PrintJobStatus;
  attempts: number;
  maxAttempts: number;
  lastAttemptTime?: number;
  errorMessage?: string;
  createdAt: number;
  completedAt?: number;
}

export interface PrintLog {
  id: string;
  jobId: string;
  orderId: string;
  timestamp: number;
  status: 'success' | 'error' | 'retry';
  message: string;
  attemptNumber: number;
  errorDetails?: string;
}

interface PrintQueueDB extends DBSchema {
  printQueue: {
    key: string;
    value: PrintJob;
    indexes: { 'by-status': PrintJobStatus; 'by-order': string; 'by-created': number };
  };
  printLogs: {
    key: string;
    value: PrintLog;
    indexes: { 'by-job': string; 'by-order': string; 'by-timestamp': number };
  };
}

const DB_NAME = 'print-queue-db';
const DB_VERSION = 1;

let dbInstance: IDBPDatabase<PrintQueueDB> | null = null;
let processingInterval: NodeJS.Timeout | null = null;

const initDB = async (): Promise<IDBPDatabase<PrintQueueDB>> => {
  if (dbInstance) return dbInstance;

  dbInstance = await openDB<PrintQueueDB>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains('printQueue')) {
        const queueStore = db.createObjectStore('printQueue', { keyPath: 'id' });
        queueStore.createIndex('by-status', 'status');
        queueStore.createIndex('by-order', 'orderId');
        queueStore.createIndex('by-created', 'createdAt');
      }

      if (!db.objectStoreNames.contains('printLogs')) {
        const logsStore = db.createObjectStore('printLogs', { keyPath: 'id' });
        logsStore.createIndex('by-job', 'jobId');
        logsStore.createIndex('by-order', 'orderId');
        logsStore.createIndex('by-timestamp', 'timestamp');
      }
    },
  });

  return dbInstance;
};

const generateId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

const log = async (
  jobId: string,
  orderId: string,
  status: 'success' | 'error' | 'retry',
  message: string,
  attemptNumber: number,
  errorDetails?: string
): Promise<void> => {
  try {
    const db = await initDB();
    const logEntry: PrintLog = {
      id: generateId(),
      jobId,
      orderId,
      timestamp: Date.now(),
      status,
      message,
      attemptNumber,
      errorDetails,
    };
    await db.add('printLogs', logEntry);
  } catch (error) {
  }
};

export const addPrintJob = async (
  orderId: string,
  ticketContent: string,
  profileId?: string,
  profileName?: string,
  copyNumber: number = 1,
  totalCopies: number = 1,
  maxAttempts: number = 5
): Promise<string> => {
  try {
    const db = await initDB();
    const jobId = generateId();

    const printJob: PrintJob = {
      id: jobId,
      orderId,
      ticketContent,
      profileId,
      profileName,
      copyNumber,
      totalCopies,
      status: 'pending',
      attempts: 0,
      maxAttempts,
      createdAt: Date.now(),
    };

    await db.add('printQueue', printJob);

    await log(
      jobId,
      orderId,
      'success',
      `Travail d'impression ajouté à la file (Copie ${copyNumber}/${totalCopies}${profileName ? ` - ${profileName}` : ''})`,
      0
    );

    startProcessing();

    return jobId;
  } catch (error) {
    throw error;
  }
};

const printWithAndroid = (ticketContent: string): boolean => {
  if (!window.android?.print) {
    return false;
  }
  try {
    // Découpage automatique des gros tickets image (voir utils/androidPrint.ts)
    return sendToAndroidPrint(ticketContent);
  } catch (error) {
    return false;
  }
};

const printWithBrowser = (ticketContent: string): boolean => {
  try {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument!;
    doc.open();
    doc.write(`
      <html>
        <head>
          <title>Ticket</title>
          <style>
            body {
              font-family: monospace;
              white-space: pre;
              font-size: 12px;
              line-height: 1.3;
            }
          </style>
        </head>
        <body>${ticketContent}</body>
      </html>
    `);
    doc.close();
    iframe.contentWindow!.focus();
    iframe.contentWindow!.print();

    setTimeout(() => {
      document.body.removeChild(iframe);
    }, 1000);

    return true;
  } catch (error) {
    return false;
  }
};

const attemptPrint = async (job: PrintJob): Promise<boolean> => {
  const isAndroid = !!window.android?.print;

  let success = false;

  if (isAndroid) {
    success = printWithAndroid(job.ticketContent);
  } else {
    success = printWithBrowser(job.ticketContent);
  }

  return success;
};

const processJob = async (job: PrintJob): Promise<void> => {
  const db = await initDB();

  try {
    job.status = 'processing';
    job.attempts += 1;
    job.lastAttemptTime = Date.now();
    await db.put('printQueue', job);

    const delayMs = Math.min(1000 * Math.pow(2, job.attempts - 1), 10000);
    if (job.attempts > 1) {
      await new Promise(resolve => setTimeout(resolve, delayMs));
    }

    const success = await attemptPrint(job);

    if (success) {
      job.status = 'completed';
      job.completedAt = Date.now();
      await db.put('printQueue', job);

      await log(
        job.id,
        job.orderId,
        'success',
        `Impression réussie (Copie ${job.copyNumber}/${job.totalCopies}${job.profileName ? ` - ${job.profileName}` : ''})`,
        job.attempts
      );
    } else {
      if (job.attempts >= job.maxAttempts) {
        job.status = 'failed';
        job.errorMessage = `Échec après ${job.maxAttempts} tentatives`;
        await db.put('printQueue', job);

        await log(
          job.id,
          job.orderId,
          'error',
          `Impression échouée définitivement après ${job.maxAttempts} tentatives`,
          job.attempts,
          job.errorMessage
        );
      } else {
        job.status = 'retrying';
        job.errorMessage = `Tentative ${job.attempts} échouée, nouvelle tentative programmée`;
        await db.put('printQueue', job);

        await log(
          job.id,
          job.orderId,
          'retry',
          `Tentative ${job.attempts}/${job.maxAttempts} échouée, nouvelle tentative dans ${Math.min(2000 * Math.pow(2, job.attempts), 10000)}ms`,
          job.attempts
        );
      }
    }
  } catch (error) {

    job.status = job.attempts >= job.maxAttempts ? 'failed' : 'retrying';
    job.errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
    await db.put('printQueue', job);

    await log(
      job.id,
      job.orderId,
      job.status === 'failed' ? 'error' : 'retry',
      `Erreur lors du traitement: ${job.errorMessage}`,
      job.attempts,
      String(error)
    );
  }
};

const processQueue = async (): Promise<void> => {
  try {
    const db = await initDB();

    const pendingJobs = await db.getAllFromIndex('printQueue', 'by-status', 'pending');
    const retryingJobs = await db.getAllFromIndex('printQueue', 'by-status', 'retrying');

    const jobsToProcess = [...pendingJobs, ...retryingJobs]
      .sort((a, b) => a.createdAt - b.createdAt);

    if (jobsToProcess.length > 0) {
      for (const job of jobsToProcess) {
        await processJob(job);

        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }
  } catch (error) {
  }
};

export const startProcessing = (): void => {
  if (processingInterval) {
    return;
  }

  processQueue();

  processingInterval = setInterval(() => {
    processQueue();
  }, 3000);
};

export const stopProcessing = (): void => {
  if (processingInterval) {
    clearInterval(processingInterval);
    processingInterval = null;
  }
};

export const getPendingJobs = async (): Promise<PrintJob[]> => {
  const db = await initDB();
  const pending = await db.getAllFromIndex('printQueue', 'by-status', 'pending');
  const processing = await db.getAllFromIndex('printQueue', 'by-status', 'processing');
  const retrying = await db.getAllFromIndex('printQueue', 'by-status', 'retrying');
  return [...pending, ...processing, ...retrying];
};

export const getCompletedJobs = async (): Promise<PrintJob[]> => {
  const db = await initDB();
  return await db.getAllFromIndex('printQueue', 'by-status', 'completed');
};

export const getFailedJobs = async (): Promise<PrintJob[]> => {
  const db = await initDB();
  return await db.getAllFromIndex('printQueue', 'by-status', 'failed');
};

export const getAllJobs = async (): Promise<PrintJob[]> => {
  const db = await initDB();
  return await db.getAll('printQueue');
};

export const getJobsByOrder = async (orderId: string): Promise<PrintJob[]> => {
  const db = await initDB();
  return await db.getAllFromIndex('printQueue', 'by-order', orderId);
};

export const getJobLogs = async (jobId: string): Promise<PrintLog[]> => {
  const db = await initDB();
  return await db.getAllFromIndex('printLogs', 'by-job', jobId);
};

export const getOrderLogs = async (orderId: string): Promise<PrintLog[]> => {
  const db = await initDB();
  return await db.getAllFromIndex('printLogs', 'by-order', orderId);
};

export const retryFailedJob = async (jobId: string): Promise<void> => {
  const db = await initDB();
  const job = await db.get('printQueue', jobId);

  if (!job) {
    throw new Error(`Job ${jobId} introuvable`);
  }

  if (job.status !== 'failed') {
    throw new Error(`Le job ${jobId} n'est pas en état 'failed'`);
  }

  job.status = 'pending';
  job.attempts = 0;
  job.errorMessage = undefined;
  job.lastAttemptTime = undefined;

  await db.put('printQueue', job);

  await log(
    jobId,
    job.orderId,
    'retry',
    'Job réinitialisé manuellement pour retry',
    job.attempts
  );

  startProcessing();
};

export const clearOldJobs = async (olderThanDays: number = 7): Promise<number> => {
  const db = await initDB();
  const cutoffTime = Date.now() - (olderThanDays * 24 * 60 * 60 * 1000);

  const allJobs = await db.getAll('printQueue');
  const jobsToDelete = allJobs.filter(job =>
    (job.status === 'completed' || job.status === 'failed') &&
    job.createdAt < cutoffTime
  );

  for (const job of jobsToDelete) {
    await db.delete('printQueue', job.id);
  }

  return jobsToDelete.length;
};

export const getQueueStats = async (): Promise<{
  total: number;
  pending: number;
  processing: number;
  retrying: number;
  completed: number;
  failed: number;
}> => {
  const db = await initDB();
  const allJobs = await db.getAll('printQueue');

  return {
    total: allJobs.length,
    pending: allJobs.filter(j => j.status === 'pending').length,
    processing: allJobs.filter(j => j.status === 'processing').length,
    retrying: allJobs.filter(j => j.status === 'retrying').length,
    completed: allJobs.filter(j => j.status === 'completed').length,
    failed: allJobs.filter(j => j.status === 'failed').length,
  };
};

if (typeof window !== 'undefined') {
  startProcessing();
}
